'use client';

import { useEffect, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import type { OccasionItem } from '@/hooks/queries/use-relationship';
import { parseLocalDate } from '@/lib/utils';

// 5x7 dot matrix patterns for digits
const PATTERNS: Record<string, number[][]> = {
  '0': [
    [0, 1, 1, 1, 0],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [0, 1, 1, 1, 0],
  ],
  '1': [
    [0, 0, 1, 0, 0],
    [0, 1, 1, 0, 0],
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
    [0, 1, 1, 1, 0],
  ],
  '2': [
    [0, 1, 1, 1, 0],
    [1, 0, 0, 0, 1],
    [0, 0, 0, 0, 1],
    [0, 0, 1, 1, 0],
    [0, 1, 0, 0, 0],
    [1, 0, 0, 0, 0],
    [1, 1, 1, 1, 1],
  ],
  '3': [
    [0, 1, 1, 1, 0],
    [1, 0, 0, 0, 1],
    [0, 0, 0, 0, 1],
    [0, 0, 1, 1, 0],
    [0, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [0, 1, 1, 1, 0],
  ],
  '4': [
    [0, 0, 0, 1, 0],
    [0, 0, 1, 1, 0],
    [0, 1, 0, 1, 0],
    [1, 0, 0, 1, 0],
    [1, 1, 1, 1, 1],
    [0, 0, 0, 1, 0],
    [0, 0, 0, 1, 0],
  ],
  '5': [
    [1, 1, 1, 1, 1],
    [1, 0, 0, 0, 0],
    [1, 1, 1, 1, 0],
    [0, 0, 0, 0, 1],
    [0, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [0, 1, 1, 1, 0],
  ],
  '6': [
    [0, 1, 1, 1, 0],
    [1, 0, 0, 0, 0],
    [1, 0, 0, 0, 0],
    [1, 1, 1, 1, 0],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [0, 1, 1, 1, 0],
  ],
  '7': [
    [1, 1, 1, 1, 1],
    [0, 0, 0, 0, 1],
    [0, 0, 0, 1, 0],
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
  ],
  '8': [
    [0, 1, 1, 1, 0],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [0, 1, 1, 1, 0],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [0, 1, 1, 1, 0],
  ],
  '9': [
    [0, 1, 1, 1, 0],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [0, 1, 1, 1, 1],
    [0, 0, 0, 0, 1],
    [0, 0, 0, 0, 1],
    [0, 1, 1, 1, 0],
  ],
};

const COLON: number[][] = [[0], [1], [0], [0], [0], [1], [0]];

function DotMatrixChar({ pattern }: { pattern: number[][] }) {
  return (
    <div className="flex flex-col gap-[1px] md:gap-[2px]">
      {pattern.map((row, i) => (
        <div key={i} className="flex gap-[1px] md:gap-[2px]">
          {row.map((dot, j) => (
            <div
              key={j}
              className={`h-[4px] w-[4px] rounded-[1px] md:h-[6px] md:w-[6px] ${
                dot ? 'bg-white' : 'bg-white/10'
              }`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function DotMatrixDisplay({
  hours,
  minutes,
  seconds,
}: {
  hours: number;
  minutes: number;
  seconds: number;
}) {
  const format = (n: number) => String(n).padStart(2, '0').split('');
  const h = format(hours);
  const m = format(minutes);
  const s = format(seconds);

  return (
    <div className="flex w-full items-center justify-center gap-2 rounded-[16px] bg-[#1a1a1a] px-5 py-4 shadow-[0_4px_16px_rgba(0,0,0,0.7)] md:gap-3 md:rounded-[20px] md:px-8 md:py-6">
      <DotMatrixChar pattern={PATTERNS[h[0] ?? '0']!} />
      <DotMatrixChar pattern={PATTERNS[h[1] ?? '0']!} />
      <div className="px-1">
        <DotMatrixChar pattern={COLON} />
      </div>
      <DotMatrixChar pattern={PATTERNS[m[0] ?? '0']!} />
      <DotMatrixChar pattern={PATTERNS[m[1] ?? '0']!} />
      <div className="px-1">
        <DotMatrixChar pattern={COLON} />
      </div>
      <DotMatrixChar pattern={PATTERNS[s[0] ?? '0']!} />
      <DotMatrixChar pattern={PATTERNS[s[1] ?? '0']!} />
    </div>
  );
}

interface CountdownCardProps {
  occasions: OccasionItem[];
}

export function CountdownCard({ occasions }: CountdownCardProps) {
  const [time, setTime] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [isToday, setIsToday] = useState(false);
  const intervalRef = useRef<number | null>(null);

  // Find next occasion
  const occasion = (() => {
    if (occasions.length === 0) {
      return null;
    }
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return [...occasions].sort((a, b) => {
      const dA = parseLocalDate(a.date);
      const dB = parseLocalDate(b.date);
      const nA = new Date(now.getFullYear(), dA.getMonth(), dA.getDate());
      const nB = new Date(now.getFullYear(), dB.getMonth(), dB.getDate());
      if (nA < today) {
        nA.setFullYear(now.getFullYear() + 1);
      }
      if (nB < today) {
        nB.setFullYear(now.getFullYear() + 1);
      }
      return nA.getTime() - nB.getTime();
    })[0];
  })();

  const targetDate = occasion?.date;

  useEffect(() => {
    if (!targetDate) {
      return;
    }

    const calculate = () => {
      const now = new Date();
      const target = parseLocalDate(targetDate);
      const next = new Date(
        now.getFullYear(),
        target.getMonth(),
        target.getDate(),
        0,
        0,
        0,
        0,
      );
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      if (next < today) {
        next.setFullYear(now.getFullYear() + 1);
      }

      const diff = next.getTime() - now.getTime();

      if (diff <= 0) {
        setIsToday(true);
        setTime({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setIsToday(false);
      setTime({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };

    calculate();
    intervalRef.current = window.setInterval(calculate, 1000);

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, [targetDate]);

  if (!occasion) {
    return null;
  }

  const formatBadgeDate = (dateStr: string) => {
    const d = parseLocalDate(dateStr);
    const day = d.getDate();
    const month = d.toLocaleDateString('pt-BR', { month: 'long' });
    return `${day} ${month}`;
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="mb-2 flex items-start justify-between">
          <span className="max-w-[70%] text-[15px] text-gray-500 dark:text-gray-400">
            {occasion.title}
          </span>
          <Badge variant="outline" className="border-primary text-primary">
            {formatBadgeDate(occasion.date)}
          </Badge>
        </div>

        <p className="mb-2 text-[24px] leading-tight font-light tracking-tight text-black md:text-[32px] dark:text-white">
          {isToday
            ? 'Hoje!'
            : time.days === 0
              ? 'Em'
              : `Em ${time.days} ${time.days === 1 ? 'dia' : 'dias'}`}
        </p>

        <div className="-mx-4">
          <DotMatrixDisplay
            hours={time.hours}
            minutes={time.minutes}
            seconds={time.seconds}
          />
        </div>
      </CardContent>
    </Card>
  );
}
